import { RefObject, useState } from 'react';

import { Box, Button, FormControl, FormLabel, Icon, Input, VStack } from '@chakra-ui/react';
import { Formik } from 'formik';
import { FaMailBulk } from 'react-icons/fa';
import { FormattedMessage, useIntl } from 'react-intl';

import UserRegisterForm from '~/components/User/UserRegisterForm';
import { firebase, useFirebaseApp } from '~/lib/firebase';

export const resetPassword = (app: firebase.app.App, email: string) => {
    return app.auth().sendPasswordResetEmail(email);
};

export const UserPasswordGotResetMessage = () => {
    return (
        <VStack minHeight="160px" justify="center" my="4">
            <Box>
                <Icon color="brand.700" w="3rem" height="3rem" as={FaMailBulk} />
            </Box>
            <Box textAlign="center">
                <FormattedMessage
                    id="user.onboarding.login_form.reset_password.success"
                    defaultMessage="Sie erhalten in Kürze eine E-Mail mit einem Link um <strong>Ihr neues Passwort</strong> zu setzen."
                />
            </Box>
        </VStack>
    );
};

const UserPasswordResetForm: React.FunctionComponent<{
    initialFocusRef?: RefObject<HTMLInputElement> | undefined;
}> = ({ initialFocusRef }) => {
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [passwordGotReset, setPasswordGotReset] = useState(false);
    const { app } = useFirebaseApp();
    const intl = useIntl();
    const emailAlreadyExists = UserRegisterForm.getEmailAlreadyExists();

    if (passwordGotReset) {
        return <UserPasswordGotResetMessage />;
    }

    return (
        <Box>
            <Formik
                initialValues={{
                    email: emailAlreadyExists
                }}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitError(null);

                    resetPassword(app, values.email)
                        .then(() => setPasswordGotReset(true))
                        .catch((e: firebase.FirebaseError) => {
                            if (e.code === 'auth/user-not-found') {
                                setSubmitError(
                                    intl.formatMessage({
                                        id: 'user.onboarding.login_form.error.user_not_found',
                                        defaultMessage: 'Sie sind noch nicht registriert.'
                                    })
                                );
                            } else {
                                setSubmitError(e.message);
                            }
                        })
                        .finally(() => setSubmitting(false));
                }}
            >
                {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                    <form onSubmit={handleSubmit}>
                        <VStack align="stretch" spacing="4">
                            <Box>
                                <FormControl id="email" width="full" isRequired>
                                    <FormLabel>
                                        <FormattedMessage
                                            id="user.onboarding.password_reset_form.field_email.label"
                                            defaultMessage="E-Mail Adresse"
                                        />
                                    </FormLabel>
                                    <Input
                                        ref={initialFocusRef}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                        type="email"
                                    />
                                </FormControl>
                            </Box>
                            <Box textAlign="center">{submitError}</Box>
                            <Box textAlign="center" paddingTop="2">
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    variant="solid"
                                    colorScheme="brand"
                                    mb="10px"
                                >
                                    <FormattedMessage
                                        id="user.onboarding.password_reset_form.submit_button"
                                        defaultMessage="Passwort zurücksetzen"
                                    />
                                </Button>
                            </Box>
                        </VStack>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

export default UserPasswordResetForm;
